import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { MainPresenter } from 'src/main/Main.presenter'

import { WaitingScope } from '../../the-connection/tc_scopes'
import AccessAuditKeys from './aa_keys'
import { AccessAuditHeaderScope, AccessAuditScope } from './aa_scopes'
import { AccessAuditService } from './aa_service'
import type { AccessAuditFilter, AccessByPageRow, AccessRankingRow } from './aa_types'

const RANKING_TOP_SIZE = 9

const LOG = Logger.get('AccessAuditPresenter')
const accessAuditService = AccessAuditService.singleton()

const DEFAULT_RANGE_DAYS = 30

export class AccessAuditPresenter extends CubePresenter<MainPresenter, AccessAuditScope> {
    // :: Fields

    private readonly __headerBar: Presenter<AccessAuditHeaderScope, AccessAuditPresenter>

    // :: Constructor

    public constructor(app: MainPresenter) {
        super(app, new AccessAuditScope())
        this.__headerBar = new Presenter(this, this.scope.header)
    }

    // :: Methods

    public override release() {
        this.app.releaseToolbar(this.__headerBar)
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(
        intent: FlipIntent,
        initialization: boolean,
        last: boolean
    ): Promise<boolean> {
        const keys = new AccessAuditKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            await this.#initialize(keys)
        }

        if (last) {
            keys.dialogSlot(undefined)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new AccessAuditKeys(this.app, intent)
        const { filterPane } = this.scope

        keys.start = filterPane.start || undefined
        keys.end = filterPane.end || undefined
        keys.groupId = filterPane.groupId
        keys.userId = filterPane.userId
        keys.placeCode = filterPane.placeCode
    }

    async #initialize(keys: AccessAuditKeys) {
        const { filterPane } = this.scope

        this.scope.monthly.caption = 'Evolução mensal'
        this.scope.daily.caption = 'Evolução diária'
        this.scope.topPages.caption = 'Relatórios mais acessados'

        filterPane.start = keys.start || defaultStart()
        filterPane.end = keys.end || today()
        filterPane.groupId = keys.groupId
        filterPane.userId = keys.userId
        filterPane.placeCode = keys.placeCode

        filterPane.onToggleExpanded = this.action(this.handleToggleExpanded)
        filterPane.onStartChanged = this.action(this.handleStartChanged)
        filterPane.onEndChanged = this.action(this.handleEndChanged)
        filterPane.onGroupChanged = this.action(this.handleGroupChanged)
        filterPane.onUserChanged = this.action(this.handleUserChanged)
        filterPane.onPageChanged = this.action(this.handlePageChanged)
        filterPane.onApply = this.action(this.handleApply)
        filterPane.onClear = this.action(this.handleClear)

        await this.#loadFilterOptions()
        await this.#load()
    }

    async #loadFilterOptions() {
        try {
            const options = await accessAuditService.fetchFilterOptions()
            this.scope.filterPane.groupOptions = options.groups
            this.scope.filterPane.userOptions = options.users
            this.scope.filterPane.pageOptions = options.pages
        } catch (caught) {
            LOG.error('loadFilterOptions', caught)
        }
    }

    async #load() {
        const filter = this.#currentFilter()

        try {
            this.scope.waiting = new WaitingScope()

            const [summary, byGroup, byUser, byPage, inactiveUsers, monthly, daily, detail] = await Promise.all([
                accessAuditService.fetchSummary(filter),
                accessAuditService.fetchByGroup(filter),
                accessAuditService.fetchByUser(filter),
                accessAuditService.fetchByPage(filter),
                accessAuditService.fetchInactiveUsers(filter),
                accessAuditService.fetchMonthly(filter),
                accessAuditService.fetchDaily(filter),
                accessAuditService.fetchDetail(filter)
            ])

            this.scope.totalGroups = summary.groups
            this.scope.totalUsers = summary.users
            this.scope.totalActiveUsers = summary.activeUsers
            this.scope.totalPages = summary.pages
            this.scope.totalAccesses = summary.accesses

            this.scope.byGroup = byGroup
            this.scope.byUser = byUser
            this.scope.byPage = byPage
            this.scope.inactiveUsers = inactiveUsers

            this.scope.monthly.rows = monthly
            this.scope.monthly.loaded = true
            this.scope.daily.rows = daily
            this.scope.daily.loaded = true

            this.scope.topPages.rows = buildRanking(byPage)
            this.scope.topPages.loaded = true

            this.scope.detailRows = detail.rows
            this.scope.detailTotal = detail.total
            this.scope.detailLimit = detail.limit
            this.scope.detailTruncated = detail.truncated
        } catch (caught) {
            LOG.error('load', caught)
            throw caught
        } finally {
            this.scope.waiting = undefined
        }
    }

    #currentFilter(): AccessAuditFilter {
        const { filterPane } = this.scope

        return {
            start: filterPane.start || undefined,
            end: filterPane.end || undefined,
            groupId: filterPane.groupId,
            userId: filterPane.userId,
            placeCode: filterPane.placeCode
        }
    }

    #syncIsFiltering() {
        const { filterPane } = this.scope
        filterPane.isFiltering = !!filterPane.groupId || !!filterPane.userId || !!filterPane.placeCode
    }

    private async handleToggleExpanded() {
        this.scope.filterPane.expanded = !this.scope.filterPane.expanded
    }

    private async handleStartChanged(value: string) {
        this.scope.filterPane.start = value
    }

    private async handleEndChanged(value: string) {
        this.scope.filterPane.end = value
    }

    private async handleGroupChanged(value?: number) {
        this.scope.filterPane.groupId = value
        this.#syncIsFiltering()
    }

    private async handleUserChanged(value?: number) {
        this.scope.filterPane.userId = value
        this.#syncIsFiltering()
    }

    private async handlePageChanged(value?: string) {
        this.scope.filterPane.placeCode = value
        this.#syncIsFiltering()
    }

    private async handleApply() {
        this.#syncIsFiltering()
        this.updateHistory()
        await this.#load()
    }

    private async handleClear() {
        const { filterPane } = this.scope

        filterPane.start = defaultStart()
        filterPane.end = today()
        filterPane.groupId = undefined
        filterPane.userId = undefined
        filterPane.placeCode = undefined

        this.#syncIsFiltering()
        this.updateHistory()
        await this.#load()
    }
}

function today(): string {
    return toIsoDate(new Date())
}

function defaultStart(): string {
    const date = new Date()
    date.setDate(date.getDate() - DEFAULT_RANGE_DAYS)
    return toIsoDate(date)
}

function buildRanking(rows: AccessByPageRow[]): AccessRankingRow[] {
    const sorted = [...rows].sort((a, b) => b.accesses - a.accesses)
    const top = sorted.slice(0, RANKING_TOP_SIZE).map((row) => ({
        label: row.pageLabel,
        accesses: row.accesses,
        isOther: false
    }))

    const rest = sorted.slice(RANKING_TOP_SIZE)
    if (rest.length > 0) {
        top.push({
            label: `Outros (${rest.length})`,
            accesses: rest.reduce((total, row) => total + row.accesses, 0),
            isOther: true
        })
    }

    return top
}

function toIsoDate(date: Date): string {
    const month = `${date.getMonth() + 1}`.padStart(2, '0')
    const day = `${date.getDate()}`.padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
}

export default AccessAuditPresenter
